import castErrorToBaseError from '@/functions/castErrorToBaseError';
import config from '@/modules/config/functions/config';
import updateLockStore from '@/modules/core/stores/update-lock';
import PromotionForm from '@/modules/promotions/components/PromotionForm';
import usePromotionFormDefaultValues from '@/modules/promotions/hooks/usePromotionFormDefaultValues';
import usePromotionFormValidationSchema from '@/modules/promotions/hooks/usePromotionFormValidationSchema';
import useEditPromotionMutation from '@/modules/promotions/mutations/useEditPromotionMutation';
import type { PromotionEditFormValues } from '@/modules/promotions/types/PromotionEditFormValues';
import { Helmet } from '@dr.pogodin/react-helmet';
import { joiResolver } from '@hookform/resolvers/joi';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ErrorPage,
  isEmptyString,
  Skeleton,
  Typography,
  useNotification,
} from '@thisisdmg/tirecloud-patterns/v2';
import { type } from 'arktype';
import { clamp } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';
import classes from './index.module.css';
import { useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_main-ui/_protected/promotions/edit/$id'
)({
  component: PromotionEditPage,
  validateSearch: type({
    s: type.string
      .or(type.number)
      .or(type.undefined)
      .pipe((s) =>
        match(s)
          .with(P.number, (x) => Math.max(0, x))
          .with(P.string, (x) => Math.max(0, ~~x))
          .otherwise(() => 0)
      )
      .default(0),
  }),
});

function PromotionEditPage() {
  const { t, i18n } = useTranslation();
  const editPromotionMutation = useEditPromotionMutation();
  const notification = useNotification();
  const navigate = useNavigate();
  const id = Route.useParams({ select: (p) => p.id });
  const _stepFromSearch = Route.useSearch({ select: (s) => s.s });
  const stepFromSearch = useMemo(
    () => clamp(_stepFromSearch, 0, 4),
    [_stepFromSearch]
  );

  useEffect(() => {
    updateLockStore.getState().lockUpdate();
    return () => updateLockStore.getState().unlockUpdate();
  }, []);

  const {
    defaultValues,
    promotionData,
    isLoadingPromotion,
    promotionFetchingError,
    refetchPromotion,
  } = usePromotionFormDefaultValues(~~id);

  const promotionEditFormValuesSchema = usePromotionFormValidationSchema();
  const queryClient = useQueryClient();

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting, isValid, ...formState },
    ...form
  } = useForm<PromotionEditFormValues>({
    mode: 'all',
    values: defaultValues,
    shouldFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(promotionEditFormValuesSchema, {
      abortEarly: false,
    }),
  });

  const submitForm: SubmitHandler<PromotionEditFormValues> = useCallback(
    async (value) => {
      return editPromotionMutation
        .mutateAsync({
          id: ~~id,
          data: value,
          defaultValues,
        })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: [
              'promotion',
              {
                params: {
                  id: ~~id,
                },
              },
            ],
          });
          notification.success({
            message: t('common.success'),
            description: t('promotions.promotion_updated'),
          });
          navigate({
            to: '/promotions',
            search: {
              ref: 'promotion_edit',
            },
          });
        })
        .catch((error) => {
          const e = castErrorToBaseError(error);
          notification.error({
            key: e.id,
            message: `0x${e.id.toString(16).toUpperCase()}`,
            description: t(`errors.gen.${e.path}`, { errorId: e.id }),
          });
        });
    },
    [
      editPromotionMutation,
      id,
      defaultValues,
      queryClient,
      notification,
      t,
      navigate,
    ]
  );

  const promotionName = useMemo(() => {
    return (
      promotionData?.data.name[i18n.language] ??
      promotionData?.data.name[config('i18n.fallbackLanguage', '')]
    );
  }, [promotionData, i18n.language]);

  return (
    <>
      {!isEmptyString(promotionName) ? (
        <Helmet titleTemplate={`%s - ${t('promotions.edit_promotion')}`}>
          <title>{promotionName}</title>
        </Helmet>
      ) : (
        <Helmet>
          <title>{t('promotions.edit_promotion')}</title>
        </Helmet>
      )}
      <div className={classes.promotion_edit_page}>
        {isLoadingPromotion ? (
          <>
            <Skeleton active />
          </>
        ) : promotionFetchingError ? (
          <div className={classes.error}>
            <ErrorPage
              code={[5, 0, 0]}
              title={t('common.oops')}
              description={t(`errors.gen.${promotionFetchingError.path}`, {
                errorId: promotionFetchingError.id,
              })}
              goBackButton={{
                label: t('common.reload'),
                onClick: () => {
                  refetchPromotion();
                },
              }}
            />
          </div>
        ) : (
          <>
            <div className={classes.header}>
              <Typography variant="hero">
                {t('promotions.edit_promotion')}
              </Typography>
            </div>
            <FormProvider
              {...{
                ...form,
                control,
                watch,
                setValue,
                handleSubmit,
                trigger,
                formState: { ...formState, errors, isSubmitting, isValid },
                errors,
              }}
            >
              <PromotionForm
                defaultValues={defaultValues}
                onSubmit={submitForm}
                isLoadingInitialValues={isLoadingPromotion}
                currentStep={stepFromSearch}
                setCurrentStep={(fn) => {
                  navigate({
                    to: '/promotions/edit/$id',
                    params: { id },
                    search: (prev) => ({ ...prev, s: fn(stepFromSearch) }),
                    replace: true,
                  });
                }}
              />
            </FormProvider>
          </>
        )}
      </div>
    </>
  );
}
